<template>
  <div class="flex flex-col gap-3">
    <div
      v-if="[Country.FRANCE, Country.MARTINIQUE].includes(default_country)"
      class="flex flex-row gap-6"
    >
      <div class="shrink-0">
        <WalkmanButton
          v-model="localPrintSettings.normalized"
          :label="$t('global.invoice_normalised')"
          :options="[
            { value: true, label: $t('enums.yesNo.1') },
            { value: false, label: $t('enums.yesNo.0') }
          ]"
        />
      </div>
      <div v-if="localPrintSettings.normalized" class="flex-1">
        <BaseShowEditInput
          v-model="localPrintSettings.nb_normalized"
          :label="$t('attributes.nb_summary')"
        />
      </div>
    </div>
    <div
      v-if="[Country.FRANCE, Country.MARTINIQUE].includes(default_country)"
      class="flex flex-row gap-6"
    >
      <div class="shrink-0">
        <WalkmanButton
          v-model="localPrintSettings.normalized_2025"
          :label="$t('global.invoice_normalised_2025_abbr')"
          :options="[
            { value: true, label: $t('enums.yesNo.1') },
            { value: false, label: $t('enums.yesNo.0') }
          ]"
        />
      </div>
      <div v-if="localPrintSettings.normalized_2025" class="flex-1">
        <BaseShowEditInput
          v-model="localPrintSettings.nb_normalized_2025"
          :label="$t('attributes.nb_summary')"
        />
      </div>
    </div>

    <div class="flex flex-row gap-6">
      <div class="shrink-0">
        <WalkmanButton
          v-model="localPrintSettings.summary"
          :label="$t('invoicing.summary')"
          :options="[
            { value: true, label: $t('enums.yesNo.1') },
            { value: false, label: $t('enums.yesNo.0') }
          ]"
        />
      </div>
      <div v-if="localPrintSettings.summary" class="flex-1">
        <BaseShowEditInput
          v-model="localPrintSettings.nb_summary"
          :label="$t('attributes.nb_summary')"
        />
      </div>
    </div>

    <div class="flex flex-row gap-6">
      <div class="shrink-0">
        <WalkmanButton
          v-model="localPrintSettings.detailed"
          :label="$t('invoicing.detailed')"
          :options="[
            { value: true, label: $t('enums.yesNo.1') },
            { value: false, label: $t('enums.yesNo.0') }
          ]"
        />
      </div>
      <div v-if="localPrintSettings.detailed" class="flex-1">
        <BaseShowEditInput
          v-model="localPrintSettings.nb_detailed"
          :label="$t('attributes.nb_summary')"
        />
      </div>
    </div>

    <div v-if="localPrintSettings.detailed" class="-mt-1">
      <div>
        <button class="text-primary" @click="customDetailed = !customDetailed">
          {{ $t('global.customize') }}
        </button>
      </div>
      <div
        v-if="customDetailed"
        class="flex flex-col gap-2 rounded-lg bg-gray-100 p-2"
      >
        <div
          v-for="(category, index) in allInvoiceCategoriesPresentInTheInvoice"
          :key="index"
        >
          <WalkmanButton
            v-if="
              invoice_styling_types &&
              localPrintSettings.category_settings[category]
            "
            v-model="localPrintSettings.category_settings[category].mode"
            :options="invoice_styling_types"
            track-by="id"
            attribute-label="name"
            size="small"
            :label="
              $trans(
                invoicing_categories.find((cat) => cat.id === category)?.name,
                {},
                '--'
              )
            "
          />
        </div>
      </div>
    </div>

    <div class="flex flex-col">
      <div class="grid w-full grid-cols-7 gap-2">
        <SelectComponent
          v-model="localPrintSettings.covering_letter_id"
          :label="$t('attributes.covering_letters')"
          name="covering_letter"
          :values="localCoveringLetters"
          :class="{
            'col-span-6': displaySaveCoveringLetterButton,
            'col-span-7': !displaySaveCoveringLetterButton
          }"
          track-by="id"
          display-key="name"
          required
        />
        <div v-if="displaySaveCoveringLetterButton" class="col-span-1">
          <BaseButton
            class="mt-2"
            :icon="'paper-plane'"
            @click="handleSaveInvoice('covering_letter')"
          />
        </div>
      </div>
    </div>
    <div class="flex flex-col">
      <div class="grid w-full grid-cols-7 gap-2">
        <SelectComponent
          v-model="localPrintSettings.footer_id"
          :label="$t('attributes.invoice_footer')"
          name="footer_id"
          :values="footers"
          :class="{
            'col-span-6': displaySaveFooterButton,
            'col-span-7': !displaySaveFooterButton
          }"
          track-by="id"
          display-key="name"
        />
        <div v-if="displaySaveFooterButton" class="col-span-1">
          <BaseButton
            class="mt-2"
            :icon="'paper-plane'"
            @click="handleSaveInvoice('footer')"
          />
        </div>
      </div>
    </div>
    <div class="flex flex-col">
      <SelectComponent
        v-model="localPrintSettings.payment_deposit_type"
        :label="$t('attributes.payment_deposit_type')"
        name="payment_deposit_type"
        :values="payment_deposit_types"
        track-by="id"
        display-key="name"
      />
    </div>
    <div class="flex flex-col">
      <SelectComponent
        v-model="localPrintSettings.payment_block_amount_mode"
        :label="$t('global.payment_block_amount_mode')"
        name="payment_block_amount_mode"
        :values="initialMetadata.enums.payment_block_amount_modes"
        track-by="id"
        display-key="name"
      />
      <div v-if="isDepositMode" class="ml-2 flex flex-col rounded text-xs">
        {{ $t('global.payment_block_amount_mode_desc') }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import WalkmanButton from '@c/WalkmanButton'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import { Country } from '@/assets/enums'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'

export default {
  name: 'TheInvoicingPrintInvoiceForm',
  components: {
    BaseButton,
    SelectComponent,
    BaseShowEditInput,
    WalkmanButton
  },
  props: {
    printSettings: {
      type: Object,
      required: true
    },
    coveringLetters: {
      type: Array,
      required: true
    },
    footers: {
      type: Array,
      required: true
    }
  },
  emits: ['update:printSettings'],
  data() {
    return {
      customDetailed: false,
      originalCoveringLetterId: null,
      originalFooterId: null,
      displaySaveCoveringLetterButton: false,
      displaySaveFooterButton: false
    }
  },
  computed: {
    ...mapGetters({
      errors: 'auth/getErrors',
      initialMetadata: 'metadata/getInitialMetadata',
      invoice: 'invoicing/getInvoice',
      defaultCountry: 'metadata/getDefaultCountry',
      invoice_styling_types: 'metadata/getInvoiceStylingTypesMetadata',
      invoicing_categories: 'metadata/getInvoicingCategoriesMetadata',
      message_texts: 'metadata/getInvoicingMessageTextsMetadata'
    }),
    Country() {
      return Country
    },
    localPrintSettings: {
      get() {
        return this.printSettings
      },
      set(value) {
        this.$emit('update:printSettings', value)
      }
    },
    default_country() {
      return this.invoice?.funeral_home?.address?.country ?? this.defaultCountry
    },
    allInvoiceCategoriesPresentInTheInvoice() {
      return [
        ...new Set(
          this.invoice?.invoice_items?.reduce((old, curr) => {
            old.push(curr.invoicing_category_id)
            return old
          }, [])
        )
      ]
    },
    isDepositMode() {
      return ['deposit_amount', 'deposit_remaining_balance'].includes(
        this.localPrintSettings.payment_block_amount_mode
      )
    },
    payment_deposit_types() {
      return [
        { id: null, name: this.$t('global.no_rib') },
        ...this.initialMetadata?.enums?.payment_deposit_types
      ]
    },
    isAfterCutoff() {
      const today = new Date()
      const cutoff = new Date('2025-07-01')
      return today >= cutoff
    },
    localCoveringLetters() {
      if (this.coveringLetters.length > 0) {
        return [
          { id: 'none', name: this.$t('global.no_covering_letter') },
          ...this.coveringLetters
        ]
      }
      return this.coveringLetters
    }
  },
  watch: {
    'localPrintSettings.covering_letter_id'(newValue, oldValue) {
      if (newValue !== oldValue && this.originalCoveringLetterId !== newValue) {
        this.invoice.covering_letter_id = newValue
        this.displaySaveCoveringLetterButton = true
      }
    },
    'localPrintSettings.footer_id'(newValue, oldValue) {
      if (newValue !== oldValue && this.originalFooterId !== newValue) {
        this.invoice.footer_id = newValue
        this.displaySaveFooterButton = true
      }
    },
    'localPrintSettings.normalized'(newValue) {
      if (!newValue) {
        if (
          !this.localPrintSettings.summary &&
          !this.localPrintSettings.detailed &&
          !this.localPrintSettings.normalized_2025
        ) {
          this.localPrintSettings.detailed = true
        }
      }
    },
    'localPrintSettings.normalized_2025'(newValue) {
      if (!newValue) {
        if (
          !this.localPrintSettings.summary &&
          !this.localPrintSettings.detailed &&
          !this.localPrintSettings.normalized
        ) {
          this.localPrintSettings.detailed = true
        }
      }
    },
    'localPrintSettings.detailed'(newValue) {
      if (!newValue) {
        if (
          !this.localPrintSettings.summary &&
          !this.localPrintSettings.normalized &&
          !this.localPrintSettings.normalized_2025
        ) {
          this.localPrintSettings[
            [Country.FRANCE].includes(this.default_country)
              ? this.isAfterCutoff
                ? 'normalized_2025'
                : 'normalized'
              : 'summary'
          ] = true
        }
      }
    },
    'localPrintSettings.summary'(newValue) {
      if (!newValue) {
        if (
          !this.localPrintSettings.normalized &&
          !this.localPrintSettings.normalized_2025 &&
          !this.localPrintSettings.detailed
        ) {
          this.localPrintSettings[
            [Country.FRANCE].includes(this.default_country)
              ? this.isAfterCutoff
                ? 'normalized_2025'
                : 'normalized'
              : 'detailed'
          ] = true
        }
      }
    }
  },
  mounted() {
    if (!this.localPrintSettings.covering_letter_id) {
      this.localPrintSettings.covering_letter_id = 'none'
    }

    this.originalCoveringLetterId =
      this.printSettings.covering_letter_id ?? 'none'
    this.originalFooterId = this.printSettings.footer_id
  },
  methods: {
    ...mapActions({
      processUpdateInvoiceAction: 'invoicing/updateInvoice'
    }),
    handleSaveInvoice(varUpated) {
      this.processUpdateInvoiceAction({
        id: this.invoice.id,
        invoice: this.invoice
      }).then((_response) => {
        if (varUpated === 'covering_letter') {
          this.$h.toastSuccess(this.$t('toasts.invoice_save_covering_letter'))
          this.displaySaveCoveringLetterButton = false
        } else {
          this.$h.toastSuccess(this.$t('toasts.invoice_save_footer'))
          this.displaySaveFooterButton = false
        }
      })
    }
  }
}
</script>
